import React, { useEffect, useMemo, useState } from 'react';
import { supabase } from '../../supabaseClient';

import Papa from 'papaparse';
import { ungzip } from 'pako';

// Inline minimal xlsx parser via dynamic import to keep bundle small when unused
let readXlsx: null | ((f: File) => Promise<{ headers: string[]; rows: any[] }>) = null;

async function loadXlsx() {
  if (readXlsx) return readXlsx;
  const XLSX: typeof import('xlsx') = await import('xlsx');
  readXlsx = async (file: File) => {
    const data = await file.arrayBuffer();
    const wb = XLSX.read(data, { type: 'array' });
    const ws = wb.Sheets[wb.SheetNames[0]];
    const json = XLSX.utils.sheet_to_json(ws, { header: 1 }) as any[][];
    const headers = (json[0] || []).map(String);
    const rows = (json.slice(1) || []).map((r) => {
      const obj: any = {};
      headers.forEach((h, i) => (obj[h] = r[i]));
      return obj;
    });
    return { headers, rows };
  };
  return readXlsx;
}

function parseJSONL(text: string): any[] {
  const rows: any[] = [];
  for (const line of text.split(/\r?\n/)) {
    const l = line.trim();
    if (!l) continue;
    try { rows.push(JSON.parse(l)); } catch { /* skip invalid lines */ }
  }
  return rows;
}

function parseTSV(text: string): { headers: string[]; rows: any[] } {
  const res = Papa.parse(text, { header: true, delimiter: '\t', skipEmptyLines: true });
  const rows = (res.data as any[]) || [];
  const headers = (res.meta.fields as string[]) || (rows[0] ? Object.keys(rows[0]) : []);
  return { headers, rows };
}

function parseTXT(text: string): { headers: string[]; rows: any[] } {
  const lines = text.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
  return { headers: ['text'], rows: lines.map(t => ({ text: t })) };
}

type CategorySet = {
  id: string;
  name: string;
  mode: 'static' | 'dynamic';
  categories: string[] | null;
  category_details: { name: string; description?: string }[] | null;
  theme_description: string | null;
};

const NewCategorization: React.FC = () => {
  // Profiles (formerly sets)
  const [sets, setSets] = useState<CategorySet[]>([]);
  const [selectedSetId, setSelectedSetId] = useState<string | 'new' | ''>('');
  const [newSetMode, setNewSetMode] = useState<'static' | 'dynamic'>('static');
  const [newSetName, setNewSetName] = useState('');
  const [newSetTheme, setNewSetTheme] = useState('');
  const [newSetDetails, setNewSetDetails] = useState<{ name: string; description?: string }[]>([{ name: '' }]);

  // Data
  const [inputKind, setInputKind] = useState<'inline' | 'file'>('inline');
  const [inlineData, setInlineData] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [previewHeaders, setPreviewHeaders] = useState<string[]>([]);
  const [previewRows, setPreviewRows] = useState<any[]>([]);
  const [selectedColumn, setSelectedColumn] = useState<string | null>(null);
  const [hoverHeader, setHoverHeader] = useState<string | null>(null);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      const { data: userData } = await supabase.auth.getUser();
      const userId = userData.user?.id;
      if (!userId) return;
      const { data } = await supabase
        .from('category_sets')
        .select('id, name, mode, categories, category_details, theme_description')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });
      setSets((data as any) || []);
    })();
  }, []);

  // Auto-select if table has a single column
  useEffect(() => {
    if (inputKind === 'file' && previewHeaders.length === 1) {
      setSelectedColumn(previewHeaders[0]);
    }
  }, [inputKind, previewHeaders]);

  const canSelectOrCreateSet = useMemo(() => {
    if (selectedSetId && selectedSetId !== 'new') return true;
    if (selectedSetId === 'new') {
      if (!newSetName.trim()) return false;
      if (newSetMode === 'static') {
        const valid = newSetDetails.filter((d) => d.name.trim()).length >= 2;
        return valid;
      }
      return newSetTheme.trim().length >= 10;
    }
    return false;
  }, [selectedSetId, newSetName, newSetMode, newSetDetails, newSetTheme]);

  const inlineArray = useMemo(() => inlineData.split('\n').map(l => l.trim()).filter(Boolean), [inlineData]);
  const canProvideData = useMemo(() => {
    if (inputKind === 'inline') return inlineArray.length > 0;
    // file: require one selected column if multiple columns exist
    if (previewHeaders.length > 1) return previewHeaders.length > 0 && !!selectedColumn && previewRows.length > 0;
    // single-column files OK
    return previewHeaders.length > 0 && previewRows.length > 0;
  }, [inputKind, inlineArray.length, previewHeaders.length, selectedColumn, previewRows.length]);

  const canSubmit = canSelectOrCreateSet && canProvideData && !submitting;

  const onFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0] || null;
    setFile(f);
    setPreviewHeaders([]);
    setPreviewRows([]);
    setSelectedColumn(null);
    if (!f) return;

    let name = f.name.toLowerCase();
    const isGz = name.endsWith('.gz');
    if (isGz) name = name.replace(/\.gz$/, '');

    const ext = name.split('.').pop() || '';
    const getText = async (): Promise<string> => {
      const buf = new Uint8Array(await f.arrayBuffer());
      const data = isGz ? new TextDecoder().decode(ungzip(buf)) : new TextDecoder().decode(buf);
      return data;
    };

    if (ext === 'csv') {
      const text = await getText();
      const res = Papa.parse(text, { header: true, skipEmptyLines: true });
      const rows = (res.data as any[]) || [];
      const headers = (res.meta.fields as string[]) || (rows[0] ? Object.keys(rows[0]) : []);
      setPreviewHeaders(headers);
      setPreviewRows(rows);
    } else if (ext === 'tsv') {
      const text = await getText();
      const { headers, rows } = parseTSV(text);
      setPreviewHeaders(headers);
      setPreviewRows(rows);
    } else if (ext === 'xlsx' || ext === 'xls') {
      const reader = await loadXlsx();
      const { headers, rows } = await reader!(f);
      setPreviewHeaders(headers.slice(0, 50));
      setPreviewRows(rows.slice(0, 20));
    } else if (ext === 'json') {
      const text = await getText();
      const json = JSON.parse(text);
      const rows = Array.isArray(json) ? json : (json.data || []);
      const headers = rows[0] ? Object.keys(rows[0]) : [];
      setPreviewHeaders(headers);
      setPreviewRows(rows.slice(0, 20));
    } else if (ext === 'jsonl' || ext === 'ndjson') {
      const text = await getText();
      const rows = parseJSONL(text);
      const headers = rows[0] ? Object.keys(rows[0]) : [];
      setPreviewHeaders(headers);
      setPreviewRows(rows.slice(0, 20));
    } else if (ext === 'txt') {
      const text = await getText();
      const { headers, rows } = parseTXT(text);
      setPreviewHeaders(headers);
      setPreviewRows(rows);
    } else {
      setError('Unsupported file type. Please upload CSV/TSV/XLSX/JSON/JSONL/TXT (optionally .gz).');
      setFile(null);
    }
  };

  const handleSubmit = async () => {
    if (inputKind === 'file' && previewHeaders.length > 1 && !selectedColumn) {
      setError('Please select one column to categorize.');
      return;
    }
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      const { data: session } = await supabase.auth.getSession();
      if (!session.session) throw new Error('Not authenticated');

      let category_set_id = selectedSetId as string;
      let isStatic = false;
      let categoriesForStatic: string[] | undefined;
      let themeDescription: string | undefined;

      if (selectedSetId === 'new') {
        const insertPayload: any = {
          name: newSetName.trim(),
          mode: newSetMode,
          theme_description: newSetMode === 'dynamic' ? newSetTheme.trim() : newSetTheme.trim() || null,
          category_details: newSetMode === 'static' ? newSetDetails.filter((d) => d.name.trim()) : null,
          categories: newSetMode === 'static' ? newSetDetails.filter((d) => d.name.trim()).map((d) => d.name.trim()) : [],
        };
        const { data, error } = await supabase.from('category_sets').insert(insertPayload).select('id').single();
        if (error) throw error;
        category_set_id = (data as any).id;
        isStatic = newSetMode === 'static';
        categoriesForStatic = isStatic ? newSetDetails.filter((d) => d.name.trim()).map(d => d.name.trim()) : undefined;
        themeDescription = !isStatic ? (newSetTheme.trim() || undefined) : undefined;
      } else {
        // Load selected profile details from local state
        const profile = sets.find(s => s.id === selectedSetId);
        isStatic = profile?.mode === 'static';
        categoriesForStatic = isStatic ? (profile?.categories || (profile?.category_details?.map(d => d.name) || [])) : undefined;
        themeDescription = !isStatic ? (profile?.theme_description || undefined) : undefined;
      }

      // Build data array
      let dataItems: string[] = [];
      if (inputKind === 'inline') {
        dataItems = inlineArray;
      } else {
        const col = selectedColumn || previewHeaders[0];
        dataItems = (previewRows || []).map((r) => (r && r[col] != null ? String(r[col]) : '')).filter((v) => v.length > 0);
      }
      if (dataItems.length === 0) throw new Error('No data items to submit.');

      // Call Edge Function
      const { data: sessionData, error: sessionErr } = await supabase.auth.getSession();
      if (sessionErr || !sessionData?.session?.access_token) throw new Error('Unable to get access token');
      const { data: userData, error: userErr } = await supabase.auth.getUser();
      if (userErr || !userData?.user?.id) throw new Error('Unable to get user');
      const userId = userData.user.id;
      const accessToken = sessionData.session.access_token;
      const baseUrl = process.env.REACT_APP_SUPABASE_URL || '';
      const fnUrl = `${baseUrl}/functions/v1/forwardRequest`;

      if (isStatic && (!categoriesForStatic || categoriesForStatic.length === 0)) {
        throw new Error('No categories defined for the selected static profile.');
      }
      if (!isStatic && !themeDescription) {
        throw new Error('The selected dynamic profile is missing a theme description.');
      }

      const requestBody: any = {
        user_id: userId,
        data: dataItems,
        categories: isStatic ? categoriesForStatic : undefined,
        is_static: isStatic,
        category_set_id,
        theme_description: themeDescription,
        input_kind: inputKind,
        input_column: selectedColumn,
        total_count: dataItems.length,
      };

      const resp = await fetch(fnUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(requestBody),
      });

      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err?.message || 'Edge function request failed');
      }

      window.location.href = '/app/category-jobs';
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to submit job');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <h3 style={{ marginTop: 0 }}>New categorization</h3>

      <div style={{ display: 'grid', gap: 16 }}>
        {/* Category profile section */}
        <div className="card" style={{ padding: 16 }}>
          <h4 style={{ marginTop: 0 }}>Category profile</h4>
          <div style={{ display: 'grid', gap: 12 }}>
            <div>
              <label className="label">Choose category profile</label>
              <select className="input" value={selectedSetId} onChange={(e) => setSelectedSetId(e.target.value as any)}>
                <option value="">Select…</option>
                <option value="new">+ Create new profile</option>
                {sets.map((s) => (
                  <option key={s.id} value={s.id}>{s.name} ({s.mode})</option>
                ))}
              </select>
            </div>

            {selectedSetId === 'new' && (
              <div style={{ display: 'grid', gap: 10 }}>
                <div style={{ display: 'grid', gap: 6 }}>
                  <label className="label">Name</label>
                  <input className="input" value={newSetName} onChange={(e) => setNewSetName(e.target.value)} placeholder="E.g. Support intents" />
                </div>
                <div style={{ display: 'grid', gap: 6 }}>
                  <label className="label">Mode</label>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button type="button" className={`button ${newSetMode==='static' ? '' : 'secondary'}`} onClick={() => setNewSetMode('static')}>Static</button>
                    <button type="button" className={`button ${newSetMode==='dynamic' ? '' : 'secondary'}`} onClick={() => setNewSetMode('dynamic')}>Dynamic</button>
                  </div>
                </div>

                {newSetMode === 'static' && (
                  <div style={{ display: 'grid', gap: 8 }}>
                    <label className="label">Categories and optional descriptions</label>
                    {newSetDetails.map((d, idx) => (
                      <div key={idx} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 8 }}>
                        <input className="input" placeholder="Category name" value={d.name} onChange={(e) => {
                          const arr = [...newSetDetails];
                          arr[idx] = { ...arr[idx], name: e.target.value };
                          setNewSetDetails(arr);
                        }} />
                        <input className="input" placeholder="Optional description" value={d.description || ''} onChange={(e) => {
                          const arr = [...newSetDetails];
                          arr[idx] = { ...arr[idx], description: e.target.value };
                          setNewSetDetails(arr);
                        }} />
                        <button type="button" className="button secondary" onClick={() => setNewSetDetails(newSetDetails.filter((_, i) => i !== idx))}>Remove</button>
                      </div>
                    ))}
                    <div>
                      <button type="button" className="button" onClick={() => setNewSetDetails([...newSetDetails, { name: '' }])}>+ Add category</button>
                    </div>
                  </div>
                )}

                {newSetMode === 'dynamic' && (
                  <div>
                    <label className="label">What are categories about?</label>
                    <textarea className="input" rows={4} placeholder="Describe the topic, e.g., Customer support intent categories" value={newSetTheme} onChange={(e) => setNewSetTheme(e.target.value)} />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Data section */}
        <div className="card" style={{ padding: 16 }}>
          <h4 style={{ marginTop: 0 }}>Data</h4>
          <div style={{ display: 'grid', gap: 12 }}>
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="button" className={`button ${inputKind==='inline' ? '' : 'secondary'}`} onClick={() => setInputKind('inline')}>Paste/Type</button>
              <button type="button" className={`button ${inputKind==='file' ? '' : 'secondary'}`} onClick={() => setInputKind('file')}>Upload File</button>
            </div>

            {inputKind === 'inline' && (
              <div>
                <label className="label">One item per line</label>
                <textarea className="input" rows={10} placeholder="Each line will be categorized" value={inlineData} onChange={(e) => setInlineData(e.target.value)} />
              </div>
            )}

            {inputKind === 'file' && (
              <div style={{ display: 'grid', gap: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <input id="fileInput" type="file" accept=".csv,.tsv,.xlsx,.xls,.json,.jsonl,.ndjson,.txt,.csv.gz,.jsonl.gz,.ndjson.gz" onChange={onFileChange} style={{ display: 'none' }} />
                  <label htmlFor="fileInput" className="button">Upload file</label>
                  {file && <span style={{ color: 'var(--color-text-muted)' }}>{file.name}</span>}
                </div>
                {previewHeaders.length > 0 && (
                  <div>
                    <div style={{ marginBottom: 8, color: 'var(--color-text-muted)' }}>Select the column to categorize by clicking a header or a column</div>
                    <div style={{ overflow: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr style={{ background: '#0f111a' }}>
                            {previewHeaders.map((h) => {
                              const isHover = hoverHeader === h;
                              const isSelected = selectedColumn === h;
                              return (
                                <th
                                  key={h}
                                  onMouseEnter={() => setHoverHeader(h)}
                                  onMouseLeave={() => setHoverHeader((prev) => (prev === h ? null : prev))}
                                  onClick={() => setSelectedColumn(h)}
                                  style={{
                                    position: 'relative',
                                    cursor: 'pointer',
                                    textAlign: 'left',
                                    padding: '12px 12px',
                                    borderBottom: '1px solid var(--color-border)',
                                    background: isSelected ? 'rgba(84,101,255,0.22)' : undefined,
                                    borderColor: isSelected ? 'rgba(84,101,255,0.55)' : 'var(--color-border)'
                                  }}
                                >
                                  {h}
                                  {(isHover || isSelected) && (
                                    <span
                                      onClick={() => setSelectedColumn(h)}
                                      style={{
                                        position: 'absolute',
                                        inset: 0,
                                        background: 'rgba(84,101,255,0.18)',
                                        cursor: 'pointer',
                                        userSelect: 'none'
                                      }}
                                    />
                                  )}
                                </th>
                              );
                            })}
                          </tr>
                        </thead>
                        <tbody>
                          {(() => {
                            const visibleRows = previewRows.slice(0, 3);
                            const middleIndex = Math.floor(visibleRows.length / 2);
                            return (
                              <>
                                {visibleRows.map((r, i) => (
                                  <tr key={i}>
                                    {previewHeaders.map((h) => {
                                      const isHover = hoverHeader === h;
                                      const isSelected = selectedColumn === h;
                                      const showPill = (isHover || isSelected) && i === middleIndex;
                                      return (
                                        <td
                                          key={h}
                                          onMouseEnter={() => setHoverHeader(h)}
                                          onMouseLeave={() => setHoverHeader((prev) => (prev === h ? null : prev))}
                                          style={{
                                            position: 'relative',
                                            padding: '8px 10px',
                                            borderBottom: '1px solid var(--color-border)',
                                            background: (isHover || isSelected) ? 'rgba(84,101,255,0.12)' : undefined
                                          }}
                                        >
                                          {String(r[h] ?? '')}
                                          {(isHover || isSelected) && (
                                            <span
                                              onClick={() => setSelectedColumn(h)}
                                              style={{
                                                position: 'absolute',
                                                inset: 0,
                                                cursor: 'pointer',
                                                background: 'transparent'
                                              }}
                                            />
                                          )}
                                          {showPill && (
                                            <span style={{
                                              position: 'absolute',
                                              left: 0,
                                              right: 0,
                                              top: 0,
                                              bottom: 0,
                                              display: 'flex',
                                              alignItems: 'center',
                                              justifyContent: 'center',
                                              pointerEvents: 'none'
                                            }}>
                                              <span style={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: 6,
                                                padding: '6px 10px',
                                                borderRadius: 999,
                                                background: 'rgba(17,18,24,0.7)',
                                                border: '1px solid rgba(84,101,255,0.5)',
                                                color: '#ffffff',
                                                fontSize: 12
                                              }}>
                                                🖱️ <span>categorize this column</span>
                                              </span>
                                            </span>
                                          )}
                                        </td>
                                      );
                                    })}
                                  </tr>
                                ))}
                                {/* Ellipsis row to indicate truncated preview */}
                                <tr>
                                  {previewHeaders.map((h) => {
                                    const isHover = hoverHeader === h;
                                    const isSelected = selectedColumn === h;
                                    return (
                                      <td
                                        key={`ellipsis-${h}`}
                                        onMouseEnter={() => setHoverHeader(h)}
                                        onMouseLeave={() => setHoverHeader((prev) => (prev === h ? null : prev))}
                                        style={{
                                          position: 'relative',
                                          padding: '8px 10px',
                                          borderBottom: '1px solid var(--color-border)',
                                          color: 'var(--color-text-muted)',
                                          fontStyle: 'italic',
                                          background: (isHover || isSelected) ? 'rgba(84,101,255,0.12)' : undefined
                                        }}
                                      >
                                        …
                                        {(isHover || isSelected) && (
                                          <span
                                            onClick={() => setSelectedColumn(h)}
                                            style={{ position: 'absolute', inset: 0, cursor: 'pointer', background: 'transparent' }}
                                          />
                                        )}
                                      </td>
                                    );
                                  })}
                                </tr>
                              </>
                            );
                          })()}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Review & submit */}
        <div className="card" style={{ padding: 16 }}>
          <h4 style={{ marginTop: 0 }}>Review & submit</h4>
          <div style={{ color: 'var(--color-text-muted)' }}>
            <div>Profile: {selectedSetId === 'new' ? `${newSetName || 'New profile'} (${newSetMode})` : sets.find(s => s.id === selectedSetId)?.name || '—'}</div>
            <div>Input: {inputKind === 'inline' ? `${inlineArray.length} lines` : file ? `${file.name}` : 'No file'}</div>
            {inputKind === 'file' && <div>Column: {selectedColumn || '—'}</div>}
          </div>
          {error && <div style={{ color: '#ff6b6b', marginTop: 8 }}>{error}</div>}
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button className="button" disabled={!canSubmit} onClick={handleSubmit}>{submitting ? 'Submitting…' : 'Submit job'}</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewCategorization; 